import { UniButton } from '@/design-system/components/buttons';
import { UniIconButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { SxProp, View, useDripsyTheme, Pressable } from 'dripsy';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UniInput, UniInputAmount } from '@/design-system/components/inputs';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { CreateCardAccountStackParamList, CreateCardAccountStackRoutes } from './type';
import { PageHeaderModalNative } from '../../../account-management/screens/page/PageHeaderModalNative';
import { Dropdown } from '@/design-system/components/dropdown';
import { CardAccountTypeSelectFilters, CardCurrencySelectFilters, CardIdentityTypeSelectFilters } from '../../options';
import { RootStackRoutes } from 'types';
import { CardAccountStackRoutes } from '../../card.navigator';
import { Icon } from '@/design-system/components/icons/Icon';
import { UseInputRegularVariant } from '@/design-system/components/inputs/useInput.hook';

export interface CreateCardAccountProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  onData: (data: any) => void;
  func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
}

export const CreateCardAccountNativeZeroScreen: React.FC<CreateCardAccountProps> = (props) => {
  const { onClose, onLayout, onData, func, onReturnData, refresh } = props;
  const { t } = useTranslation('card-account');
  const { body, textMb, btnView, btn, textTitle, mobileView, mobileCodeView, mobileNumberView, inputSx, account } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<CreateCardAccountStackParamList>>();
  const navigationAny = useNavigation<any>();

  const onBackData = (data: any) => {
    setCardAccount({ ...cardAccount, ...data });
  };
  const [cardAccount, setCardAccount] = useState<any>({
    name: undefined,
    phoneCode: undefined,
    phoneNumber: undefined,
    identityType: 'individual',
    accountType: 'asset-link',
    currency: 'HKD',
    assetLink: undefined,
    creditAccount: undefined,
    limit: undefined,
    settlementAccount: undefined
  });
  const [errorMsg, setErrorMsg] = useState<any>({
    name: undefined,
    phoneCode: undefined,
    phoneNumber: undefined,
    phoneCodeNumber: undefined,
    assetLink: undefined,
    creditAccount: undefined,
    limit: undefined,
    settlementAccount: undefined
  });

  const validateForm = async (data?: { [key: string]: string | undefined }) => {
    const keys = data ? Object.keys(data) : [];
    const keysLength = keys.length;
    let validateFlag = true;
    let errorMsgFlag = { ...errorMsg };

    if ((!keysLength && !cardAccount.name) || (keys.includes('name') && !data?.name)) {
      errorMsgFlag.name = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount.name) || (keys.includes('name') && data?.name)) {
      errorMsgFlag.name = undefined;
    }

    if ((!keysLength && !cardAccount.phoneCode) || (keys.includes('phoneCode') && !data?.phoneCode)) {
      errorMsgFlag.phoneCode = ' ';
      errorMsgFlag.phoneCodeNumber = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount.phoneCode) || (keys.includes('phoneCode') && data?.phoneCode)) {
      errorMsgFlag.phoneCode = undefined;
      errorMsgFlag.phoneCodeNumber = undefined;
    }

    if ((!keysLength && !cardAccount.phoneNumber) || (keys.includes('phoneNumber') && !data?.phoneNumber)) {
      errorMsgFlag.phoneNumber = ' ';
      errorMsgFlag.phoneCodeNumber = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && cardAccount.phoneNumber) || (keys.includes('phoneNumber') && data?.phoneNumber)) {
      errorMsgFlag.phoneNumber = undefined;
      errorMsgFlag.phoneCodeNumber = undefined;
    }

    if (
      ((!keysLength && !cardAccount.assetLink) || (keys.includes('assetLink') && !data?.assetLink)) &&
      cardAccount.accountType == 'asset-link'
    ) {
      errorMsgFlag.assetLink = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if (
      ((!keysLength && cardAccount.assetLink) || (keys.includes('assetLink') && data?.assetLink)) &&
      cardAccount.accountType == 'asset-link'
    ) {
      errorMsgFlag.assetLink = undefined;
    }

    if (
      ((!keysLength && !cardAccount.creditAccount) || (keys.includes('creditAccount') && !data?.creditAccount)) &&
      cardAccount.accountType == 'fixed-limit'
    ) {
      errorMsgFlag.creditAccount = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if (
      ((!keysLength && cardAccount.creditAccount) || (keys.includes('creditAccount') && data?.creditAccount)) &&
      cardAccount.creditAccount == 'fixed-limit'
    ) {
      errorMsgFlag.creditAccount = undefined;
    }

    if (((!keysLength && !cardAccount.limit) || (keys.includes('limit') && !data?.limit)) && cardAccount.accountType == 'fixed-limit') {
      errorMsgFlag.limit = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if (((!keysLength && cardAccount.limit) || (keys.includes('limit') && data?.limit)) && cardAccount.limit == 'fixed-limit') {
      errorMsgFlag.limit = undefined;
    }

    if (
      ((!keysLength && !cardAccount.settlementAccount) || (keys.includes('settlementAccount') && !data?.settlementAccount)) &&
      cardAccount.accountType == 'fixed-limit'
    ) {
      errorMsgFlag.settlementAccount = t('card-account::credit-card-new-account.glossary.field-required');
      validateFlag = false;
    } else if (
      ((!keysLength && cardAccount.settlementAccount) || (keys.includes('settlementAccount') && data?.settlementAccount)) &&
      cardAccount.settlementAccount == 'fixed-limit'
    ) {
      errorMsgFlag.settlementAccount = undefined;
    }

    setErrorMsg(errorMsgFlag);
    return validateFlag;
  };

  const changeCreateCardAccount = async () => {
    const validate = await validateForm();
    if (validate) {
      onData({ data: cardAccount });
      if (cardAccount.identityType == 'individual') {
        navigation.navigate(CreateCardAccountStackRoutes.Individual, {
          onLayout,
          data: cardAccount,
          onData,
          func,
          onReturnData,
          refresh,
          onBackData
        });
      } else {
        navigation.navigate(CreateCardAccountStackRoutes.Confirm, {
          onLayout,
          data: cardAccount,
          func,
          onReturnData,
          refresh
        });
      }
    }
  };

  const { theme } = useDripsyTheme();

  return (
    <View sx={{ backgroundColor: '$backgroundPrimary', width: '100%' }}>
      <PageHeaderModalNative>
        <View sx={{ px: '$4', mt: '$5', mb: '$4' }}>
          <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
            <UniIconButton
              icon="arrowLeft"
              options={{ iconStyles: { color: theme?.colors?.$white as string } }}
              onPress={onClose}></UniIconButton>
          </View>
          <View sx={{ textAlign: 'center' }}>
            <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
              {t('card-account::credit-card-new-account.glossary.new-account')}
            </TextTitle>
          </View>
        </View>
      </PageHeaderModalNative>
      <View sx={body}>
        <TextTitle sx={textMb} semiBold>
          {t('card-account::credit-card-new-account.new-account-page.text--fill-out-to-create-account')}
        </TextTitle>
        <View sx={{ gap: '$5' }}>
          <UniInput
            value={cardAccount.name}
            labelText={t('card-account::credit-card-new-account.glossary.account-name')}
            errorText={errorMsg.name}
            onValueChange={(value) => {
              setCardAccount({
                ...cardAccount,
                name: value
              });
              validateForm({ name: value });
            }}
          />
          <View sx={{ gap: '$1' }}>
            <TextCaption sx={textTitle} semiBold>
              {t('card-account::credit-card-new-account.glossary.mobile-number')}
            </TextCaption>
            <View sx={mobileView}>
              <View sx={mobileCodeView}>
                <UniInput
                  regular={UseInputRegularVariant.number}
                  size="medium"
                  sx={inputSx}
                  value={cardAccount?.phoneCode}
                  errorText={errorMsg.phoneCode}
                  onValueChange={(value) => {
                    setCardAccount({ ...cardAccount, phoneCode: value });
                    validateForm({ phoneCode: value });
                  }}
                />
              </View>
              <View sx={mobileNumberView}>
                <UniInput
                  regular={UseInputRegularVariant.number}
                  size="medium"
                  sx={inputSx}
                  value={cardAccount?.phoneNumber}
                  errorText={errorMsg.phoneNumber}
                  onValueChange={(value) => {
                    setCardAccount({ ...cardAccount, phoneNumber: value });
                    validateForm({ phoneNumber: value });
                  }}
                />
              </View>
            </View>
            {errorMsg?.phoneCodeNumber && (
              <TextCaption danger sx={{ mt: '$1' }}>
                {errorMsg?.phoneCodeNumber}
              </TextCaption>
            )}
          </View>
          <View sx={{ zIndex: 99 }}>
            <Dropdown
              value={cardAccount.identityType}
              label={t('card-account::credit-card-new-account.glossary.account-type')}
              options={CardIdentityTypeSelectFilters(t)}
              sx={{ width: '100%' }}
              onValueChange={(value) => {
                setCardAccount({
                  ...cardAccount,
                  identityType: value
                });
              }}
            />
          </View>
          <View sx={{ zIndex: 98 }}>
            <Dropdown
              value={cardAccount.accountType}
              label={t('card-account::credit-card-new-account.glossary.card-type')}
              options={CardAccountTypeSelectFilters(t)}
              sx={{ width: '100%' }}
              onValueChange={async (value) => {
                let cardAccountDemo = {
                  ...cardAccount,
                  accountType: value
                };
                if (value != 'asset-link') {
                  cardAccountDemo.assetLink = undefined;
                  await setErrorMsg({ ...errorMsg, assetLink: undefined });
                }
                if (value != 'fixed-limit') {
                  cardAccountDemo.creditAccount = undefined;
                  cardAccountDemo.limit = undefined;
                  cardAccountDemo.settlementAccount = undefined;
                  await setErrorMsg({ ...errorMsg, creditAccount: undefined, limit: undefined, settlementAccount: undefined });
                }
                setCardAccount(cardAccountDemo);
              }}
            />
          </View>
          <View sx={{ zIndex: 97 }}>
            <Dropdown
              value={cardAccount.currency}
              label={t('card-account::credit-card-new-account.glossary.currency')}
              options={CardCurrencySelectFilters(t)}
              sx={{ width: '100%' }}
              onValueChange={(value) => {
                setCardAccount({
                  ...cardAccount,
                  currency: value
                });
              }}
            />
          </View>
          {cardAccount.accountType == 'asset-link' && (
            <View sx={{ gap: 4 }}>
              <TextCaption semiBold>{t('card-account::credit-card-new-account.glossary.account')}</TextCaption>
              <Pressable
                onPress={() => {
                  navigationAny.navigate(RootStackRoutes.CardAccount, {
                    screen: CardAccountStackRoutes.CreateCardAccount,
                    params: {
                      screen: CreateCardAccountStackRoutes.SelectAccount,
                      onLayout,
                      onSelect: (data: any) => {
                        setCardAccount({ ...cardAccount, assetLink: data });
                        setErrorMsg({ ...errorMsg, assetLink: undefined });
                      }
                    }
                  });
                }}>
                <View sx={{ ...account, ...(cardAccount.assetLink ? { flexDirection: 'column', py: '$2' } : {}) }}>
                  <View
                    sx={{
                      justifyContent: 'space-between',
                      display: 'flex',
                      width: '100%',
                      flexDirection: 'row',
                      alignItems: 'center'
                    }}>
                    {cardAccount.assetLink ? (
                      <View sx={{ gap: '$1' }}>
                        <TextBody semiBold sx={{ color: '$textPrimary' }}>
                          {cardAccount.assetLink?.name}
                        </TextBody>
                        <TextBody sx={{ color: '$textPrimary' }}>{cardAccount.assetLink?.accountNumber}</TextBody>
                      </View>
                    ) : (
                      <TextCaption sx={{ color: '$borderUpload' }}>
                        {t('card-account::credit-card-new-account.glossary.select-account')}
                      </TextCaption>
                    )}
                    <Icon name="arrowRight" />
                  </View>
                </View>
              </Pressable>
              {errorMsg?.assetLink && (
                <TextCaption danger sx={{ mt: '$1' }}>
                  {errorMsg?.assetLink}
                </TextCaption>
              )}
            </View>
          )}
          {cardAccount.accountType == 'fixed-limit' && (
            <View sx={{ gap: 4 }}>
              <TextCaption semiBold>{t('card-account::credit-card-new-account.glossary.credit-account')}</TextCaption>
              <Pressable
                onPress={() => {
                  navigationAny.navigate(RootStackRoutes.CardAccount, {
                    screen: CardAccountStackRoutes.CreateCardAccount,
                    params: {
                      screen: CreateCardAccountStackRoutes.SelectAccount,
                      onLayout,
                      onSelect: (data: any) => {
                        setCardAccount({ ...cardAccount, creditAccount: data });
                        setErrorMsg({ ...errorMsg, creditAccount: undefined });
                      }
                    }
                  });
                }}>
                <View sx={{ ...account, ...(cardAccount.creditAccount ? { flexDirection: 'column', py: '$2' } : {}) }}>
                  <View
                    sx={{
                      justifyContent: 'space-between',
                      display: 'flex',
                      width: '100%',
                      flexDirection: 'row',
                      alignItems: 'center'
                    }}>
                    {cardAccount.creditAccount ? (
                      <View sx={{ gap: '$1' }}>
                        <TextBody semiBold sx={{ color: '$textPrimary' }}>
                          {cardAccount.creditAccount?.name}
                        </TextBody>
                        <TextBody sx={{ color: '$textPrimary' }}>{cardAccount.creditAccount?.accountNumber}</TextBody>
                      </View>
                    ) : (
                      <TextCaption sx={{ color: '$borderUpload' }}>
                        {t('card-account::credit-card-new-account.glossary.select-account')}
                      </TextCaption>
                    )}
                    <Icon name="arrowRight" />
                  </View>
                </View>
              </Pressable>
              {errorMsg?.creditAccount && (
                <TextCaption danger sx={{ mt: '$1' }}>
                  {errorMsg?.creditAccount}
                </TextCaption>
              )}
            </View>
          )}
          {cardAccount.accountType == 'fixed-limit' && (
            <UniInputAmount
              labelText={t('card-account::credit-card-new-account.glossary.limit')}
              assetPrefix={false}
              maxFlag={false}
              sxWrap={{ alignItems: 'center' }}
              asset={cardAccount?.currency}
              errorText={errorMsg.limit}
              value={cardAccount?.limit || 0}
              onValueChange={(value) => {
                setCardAccount({ ...cardAccount, limit: value });
                setErrorMsg({ ...errorMsg, limit: undefined });
              }}
            />
          )}
          {cardAccount.accountType == 'fixed-limit' && (
            <View sx={{ gap: 4 }}>
              <TextCaption semiBold>{t('card-account::credit-card-new-account.glossary.settlement-account')}</TextCaption>
              <Pressable
                onPress={() => {
                  navigationAny.navigate(RootStackRoutes.CardAccount, {
                    screen: CardAccountStackRoutes.CreateCardAccount,
                    params: {
                      screen: CreateCardAccountStackRoutes.SelectAccount,
                      onLayout,
                      onSelect: (data: any) => {
                        setCardAccount({ ...cardAccount, settlementAccount: data });
                        setErrorMsg({ ...errorMsg, settlementAccount: undefined });
                      }
                    }
                  });
                }}>
                <View sx={{ ...account, ...(cardAccount.settlementAccount ? { flexDirection: 'column', py: '$2' } : {}) }}>
                  <View
                    sx={{
                      justifyContent: 'space-between',
                      display: 'flex',
                      width: '100%',
                      flexDirection: 'row',
                      alignItems: 'center'
                    }}>
                    {cardAccount.settlementAccount ? (
                      <View sx={{ gap: '$1' }}>
                        <TextBody semiBold sx={{ color: '$textPrimary' }}>
                          {cardAccount.settlementAccount?.name}
                        </TextBody>
                        <TextBody sx={{ color: '$textPrimary' }}>{cardAccount.settlementAccount?.accountNumber}</TextBody>
                      </View>
                    ) : (
                      <TextCaption sx={{ color: '$borderUpload' }}>
                        {t('card-account::credit-card-new-account.glossary.select-account')}
                      </TextCaption>
                    )}
                    <Icon name="arrowRight" />
                  </View>
                </View>
              </Pressable>
              {errorMsg?.settlementAccount && (
                <TextCaption danger sx={{ mt: '$1' }}>
                  {errorMsg?.settlementAccount}
                </TextCaption>
              )}
            </View>
          )}
        </View>
        <View sx={{ ...btnView, zIndex: -1 }}>
          <UniButton
            sx={btn}
            labelSx={{ color: '$balck' }}
            label={
              cardAccount.identityType == 'individual'
                ? t('card-account::credit-card-new-account.glossary.continue')
                : t('card-account::credit-card-new-account.glossary.new-account')
            }
            onPress={changeCreateCardAccount}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    px: 16,
    py: 20
  },
  textMb: {
    marginBottom: 20
  },
  btnView: {
    width: '100%',
    left: 0,
    bottom: 0,
    pt: '$10'
  },
  btn: {
    width: '100%',
    height: 44
  },
  textTitle: {
    marginBottom: '$1'
  },
  mobileView: {
    flexDirection: 'row',
    flex: 'auto',
    height: '$11'
  },
  mobileCodeView: {
    width: '30%',
    mr: '$2'
  },
  inputSx: {
    width: '100%'
  },
  mobileNumberView: {
    width: '68%'
  },
  account: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    py: '$5',
    px: '$4',
    borderWidth: 1,
    borderColor: '#DCDCDC',
    borderRadius: 5
  }
};
